import { FileAddFilled } from '@ant-design/icons';
import { App, Button, Card, DatePicker, Spin, Table, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import type React from 'react';
import { useState } from 'react';
import { useTableSkeleton } from '@/components/skeletons/useTableSkeleton';
import { AppModal } from '@/components/ui/AppModal';
import type { GetCountryListQuery } from '@/generated/graphql-types';
import dayjs from '../../utils/dayjs-instance';
import {
  useDeleteCountryHoliday,
  useGetCountryHolidays,
  useInsertCountryHoliday,
} from './query/countryHolidays.hooks';

export const CountryHolidayManager: React.FC = () => {
  const { message, modal } = App.useApp();
  const { data: countryHolidays, isLoading: countryHolidaysLoading } = useGetCountryHolidays();
  const { mutateAsync: insertCountryHoliday, isPending: insertCountryHolidayPending } =
    useInsertCountryHoliday();
  const { mutateAsync: deleteCountryHoliday } = useDeleteCountryHoliday();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [deletingHolidayId, setDeletingHolidayId] = useState<number | null>(null);

  const handleAddHoliday = async () => {
    if (!selectedCountry) {
      message.error('Please select a country');
      return;
    }

    try {
      await insertCountryHoliday({
        countryId: selectedCountry,
        date: selectedDate?.format('YYYY-MM-DD') ?? '',
      });
      setIsModalVisible(false);

      message.success('Holiday added successfully');
    } catch (_error) {
      message.error('Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    modal.confirm({
      title: 'Are you sure you want to delete this holiday?',
      cancelButtonProps: { disabled: deletingHolidayId !== null },
      onOk: async () => {
        setDeletingHolidayId(id);
        try {
          await deleteCountryHoliday({ id });
          message.success('Holiday deleted successfully');
        } catch (_error) {
          message.error('Failed to delete holiday');
        } finally {
          setDeletingHolidayId(null);
        }
      },
    });
  };

  const handleAddClick = (countryId: string) => {
    setSelectedCountry(countryId);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Country',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Holidays',
      dataIndex: 'country_holidays',
      key: 'holidays',
      render: (
        holidays: GetCountryListQuery['country'][0]['country_holidays'],
        record: GetCountryListQuery['country'][0]
      ) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {holidays
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map((holiday) => (
              <Tag
                key={holiday.id}
                closable={!deletingHolidayId || deletingHolidayId !== holiday.id}
                onClose={async (e) => {
                  e.preventDefault();
                  await handleDeleteHoliday(holiday.id);
                }}
                color={dayjs(holiday.date).isSameOrAfter(dayjs(), 'day') ? 'blue' : 'red'}
              >
                {dayjs(holiday.date).utc().format('YYYY.MM.DD')}
                {deletingHolidayId === holiday.id && <Spin size="small" />}
              </Tag>
            ))}
          <Button
            size={'small'}
            icon={<FileAddFilled />}
            type="dashed"
            onClick={() => handleAddClick(record.id)}
          >
            Add
          </Button>
        </div>
      ),
    },
  ];

  const disabledDates = (current: Dayjs): boolean => {
    const holidays = countryHolidays?.country
      .find((country: GetCountryListQuery['country'][0]) => country.id === selectedCountry)
      ?.country_holidays.map((holiday: GetCountryListQuery['country'][0]['country_holidays'][0]) =>
        dayjs(holiday.date, 'YYYY-MM-DD')
      );

    if (current) {
      if (current < dayjs().startOf('day')) {
        return true;
      }
      if (holidays?.some((date: Dayjs) => date.isSame(current, 'day'))) {
        return true;
      }
    }

    return false;
  };

  const tableSkeleton = useTableSkeleton(columns, countryHolidays?.country, countryHolidaysLoading);

  return (
    <div style={{ padding: 16 }}>
      <Table
        className="app-table"
        rowKey="id"
        dataSource={tableSkeleton.dataSource}
        columns={tableSkeleton.columns}
        pagination={false}
        loading={tableSkeleton.loading}
      />
      <Card size={'small'} style={{ marginTop: 6 }}>
        <div>
          <Tag color={'blue'}>{dayjs().add(10, 'day').format('YYYY-MM-DD')}</Tag>
          <span>The holiday is in the future</span>
        </div>
        <div style={{ marginTop: 6 }}>
          <Tag color={'red'}>{dayjs().subtract(10, 'day').format('YYYY-MM-DD')}</Tag>
          <span>The holiday is in the past</span>
        </div>
      </Card>
      <AppModal
        title="Add Holiday"
        open={isModalVisible}
        onOk={handleAddHoliday}
        onCancel={() => setIsModalVisible(false)}
        okButtonProps={{
          disabled: !selectedDate || insertCountryHolidayPending,
          loading: insertCountryHolidayPending,
        }}
        cancelButtonProps={{ disabled: insertCountryHolidayPending }}
      >
        <h3>
          Country:{' '}
          {
            countryHolidays?.country.find(
              (country: GetCountryListQuery['country'][0]) => country.id === selectedCountry
            )?.name
          }
        </h3>
        <DatePicker
          style={{ width: '100%' }}
          onChange={(date) => setSelectedDate(date)}
          disabledDate={disabledDates}
        />
      </AppModal>
    </div>
  );
};
